import { useReadCompanyRevenue } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { formatCurrency } from '@/helpers'
import { MonthsEnum } from '@/types'
import { BarChart, BarChartProps } from '@mui/x-charts/BarChart'
import { Box, CircularProgress, useTheme } from '@mui/material'
import { getMonth, getYear } from 'date-fns'
import { useCallback } from 'react'

type RevenueChartProps = Omit<
  BarChartProps,
  'series' | 'dataset' | 'xAxis' | 'yAxis'
> & {
  mode?: 'light' | 'dark'
  month?: number
  year?: number
}

export function RevenueChart({
  mode = 'light',
  month,
  year,
  ...props
}: RevenueChartProps) {
  const theme = useTheme()

  const { company } = useCompany()

  const { data: revenue, isLoading } = useReadCompanyRevenue({
    company_key: company.key,
    year,
  })

  const generateAllMonthsRevenue = useCallback(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const month = i + 1
        const monthRevenue = revenue?.find((item) => item.month === month)

        return {
          month: monthRevenue?.month || month,
          year: monthRevenue?.year || getYear(new Date()),
          revenue: monthRevenue?.revenue || 0,
        }
      }),
    [revenue],
  )

  const yearRevenue = generateAllMonthsRevenue()

  const generateBarColors = useCallback(() => {
    const currentMonth = getMonth(new Date()) + 1

    const selectedMonth = month || currentMonth

    return yearRevenue.map((item) => {
      if (item.month === selectedMonth) {
        return theme.palette.secondary.main
      }

      return mode === 'dark' ? theme.palette.primary.light : '#fff'
    })
  }, [month])

  if (isLoading) {
    return <Loading />
  }

  if (!revenue) {
    return <p>Não foi possível carregar o faturamento</p>
  }

  return (
    <BarChart
      {...props}
      dataset={yearRevenue}
      xAxis={[
        {
          scaleType: 'band',
          dataKey: 'month',
          valueFormatter: (value: number, context) =>
            context.location === 'tick'
              ? value.toString().padStart(2, '0')
              : MonthsEnum[value],
          colorMap: {
            type: 'ordinal',
            colors: generateBarColors(),
          },
        },
      ]}
      series={[
        {
          dataKey: 'revenue',
          label: 'Faturamento',
          valueFormatter: (value) => formatCurrency(value || 0),
          type: 'bar',
        },
      ]}
      bottomAxis={{
        disableLine: true,
        disableTicks: true,
      }}
      leftAxis={{ disableLine: true, disableTicks: true }}
      slotProps={{ legend: { hidden: true } }}
      borderRadius={6}
      margin={{
        left: 0,
        right: 0,
        top: 0,
        bottom: 24,
      }}
    />
  )
}

const Loading = () => {
  return (
    <Box
      height={160}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <CircularProgress size={24} sx={{ color: 'white' }} />
    </Box>
  )
}
